import { CarouselTray } from '../../../domain/carousel-tray';
import {
  BannersPageItemApiModel,
  CreateBannersPageItemRequestApiModel,
  SubTypeApiModel,
  UpdateBannersPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import {
  commonMapper,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { NewCarouselTrayFormViewModel } from '../new-carousel-tray-form/new-carousel-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  commonMapperDomainToViewModel,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  commonMapperViewModelToDomain,
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

export const mapNewCarouselTrayDomainModelToApiModel = (
  item: CarouselTray,
  pageVersionId?: string,
  tenantId?: string
): CreateBannersPageItemRequestApiModel & UpdateBannersPageItemRequestApiModel => {
  const commonProperties = mapperPipe(item, nameLocalizedMapperDomainToApi, targetingMapperDomainToApi, scheduledDateMapperDomainToApi);
  const { subtype, feedId, contentGroup, items } = item;

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    subType: subtype,
    ...(subtype === SubTypeApiModel.OsnPlusContentGroup && { feedId }),
    ...((subtype === SubTypeApiModel.OndemandContentGroup || subtype === SubTypeApiModel.StoreContentGroup) && {
      contentGroupId: contentGroup.id,
    }),
    ...(subtype === SubTypeApiModel.LiveTvProgram && {
      items: items?.map((trayItem, idx) => {
        const { channel, programStartTime, programEndTime, programId, instanceId } = trayItem;

        return {
          order: idx + 1,
          channelId: channel.id,
          programmingTime: programStartTime ? new Date(programStartTime).getTime() : undefined,
          programmingEndTime: programEndTime ? new Date(programEndTime).getTime() : undefined,
          programId,
          instanceId,
        };
      }),
    }),

    backgroundImage: item.backgroundImage.id,
    tenantId,
  }));
};

export const mapNewCarouselTrayApiModelToDomainModel = (pageItem: BannersPageItemApiModel): CarouselTray => {
  const { subType } = pageItem;

  const { feedId, contentGroup, items, backgroundImage } = pageItem;

  return remainderMapper(
    mapperPipe(pageItem, commonMapper, nameLocalizedMapperApiToDomain, targetingMapperApiToDomain, scheduledDateMapperApiToDomain),
    () => ({
      type: 'CarouselTray',
      subtype: subType,
      ...(subType === SubTypeApiModel.OsnPlusContentGroup && { feedId }),
      ...((subType === SubTypeApiModel.OndemandContentGroup || subType === SubTypeApiModel.StoreContentGroup) && {
        contentGroup: {
          id: contentGroup.id,
          name: contentGroup.title,
          modified: contentGroup.updatedAt,
          referringPages: contentGroup.referringPageNames || [],
        },
      }),
      ...(subType === SubTypeApiModel.LiveTvProgram && {
        items: items?.map((item) => {
          const { channel, programmingTime, programmingEndTime, programId, instanceId } = item;

          return {
            channel: channel
              ? {
                  id: channel.id,
                  channelID: channel.channelId,
                  channelName: channel.channelName,
                  channelNumber: channel.channelNumber,
                }
              : undefined,

            programStartTime: new Date(programmingTime).toISOString(),
            programEndTime: new Date(programmingEndTime).toISOString(),
            programId,
            instanceId,
          };
        }),
      }),
      backgroundImage: {
        id: backgroundImage.id,
        name: backgroundImage.originalName,
        publicUrl: backgroundImage.publicUrl,
      },
    })
  );
};

export const mapNewCarouselTrayDomainModelToViewModel = (pageItem: CarouselTray): NewCarouselTrayFormViewModel => {
  const commonProperties = mapperPipe(
    pageItem,
    commonMapperDomainToViewModel,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel
  );

  const { subtype, backgroundImage, feedId, contentGroup, items } = pageItem;

  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.BannersThree,
    subType: subtype,
    backgroundImage,
    ...(subtype === SubTypeApiModel.OsnPlusContentGroup && { feedId }),
    ...((subtype === SubTypeApiModel.OndemandContentGroup || subtype === SubTypeApiModel.StoreContentGroup) && { contentGroup }),
    ...(subtype === SubTypeApiModel.LiveTvProgram && {
      items: items.map((item) => {
        const { channel, programId, programStartTime, programEndTime, instanceId } = item;
        return {
          programSelector: {
            channel,
            programId,
            programStartTime: new Date(programStartTime).getTime(),
            programEndTime: new Date(programEndTime).getTime(),
            instanceId,
          },
        };
      }),
    }),
  }));
};

export const mapNewCarouselTrayViewModelToDomainModel = (vm: NewCarouselTrayFormViewModel): CarouselTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain
  );

  const { subType, feedId, contentGroup, items, backgroundImage } = vm;

  return remainderMapper(commonProperties, () => ({
    type: 'CarouselTray',
    subtype: subType,
    feedId: subType === SubTypeApiModel.OsnPlusContentGroup ? feedId : undefined,
    contentGroup:
      subType === SubTypeApiModel.OndemandContentGroup || subType === SubTypeApiModel.StoreContentGroup ? contentGroup : undefined,
    items:
      subType === SubTypeApiModel.LiveTvProgram
        ? items.map((item) => {
            const { programSelector } = item;

            return {
              channel: programSelector.channel,
              programId: programSelector.programId,
              instanceId: programSelector.instanceId,
              programStartTime: new Date(programSelector.programStartTime).toISOString(),
              programEndTime: new Date(programSelector.programEndTime).toISOString(),
            };
          })
        : undefined,
    backgroundImage,
  }));
};
